import React, { useEffect, useState } from 'react';
import { departmentApi } from '@/features/workspace/api';
import { PlusSymbolIcon, CloseCrossIcon } from './DepartmentIcons';
import { useToast } from '@/hooks/useToast';

export const DepartmentDocumentsView: React.FC = () => {
  const { notify } = useToast();
  const [docs, setDocs] = useState<any[]>([]);
  const [, setLoading] = useState<boolean>(true);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newFilename, setNewFilename] = useState<string>('');
  const [newFileType, setNewFileType] = useState<string>('DOCX');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const fetchDocs = async () => {
    try {
      setLoading(true);
      const res = await departmentApi.documents();
      setDocs(res);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, []);

  const handleAddDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFilename.trim()) return;
    try {
      setSubmitting(true);
      await departmentApi.createDocument({
        filename: newFilename.trim(),
        file_type: newFileType,
        size_label: '240 KB',
      });
      setNewFilename('');
      setShowAddModal(false);
      fetchDocs();
    } catch {
      notify('Ошибка при добавлении документа', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplace = async (docId: string) => {
    try {
      await departmentApi.replaceDocument(docId, {});
      fetchDocs();
    } catch {
      notify('Не удалось обновить версию документа', 'error');
    }
  };

  const handleDelete = async (docId: string) => {
    if (!window.confirm('Удалить документ из реестра?')) return;
    try {
      await departmentApi.deleteDocument(docId);
      fetchDocs();
    } catch {
      notify('Ошибка при удалении документа', 'error');
    }
  };

  const totalFiles = docs.length;
  const xlsxCount = docs.filter((d) => d.file_type?.toUpperCase() === 'XLSX').length;
  const pdfCount = docs.filter((d) => d.file_type?.toUpperCase() === 'PDF').length;
  const docxCount = docs.filter((d) => d.file_type?.toUpperCase() === 'DOCX').length;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-6">
        <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
          Department Documents
        </span>
        <h2 className="text-2xl font-bold text-white tracking-tight mt-1">
          Документы кафедры
        </h2>
        <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-4xl">
          Единый реестр рабочих файлов кафедры. В прототипе сохраняются метаданные файла и версия.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Файлов</span>
            <div className="text-3xl font-extrabold text-white mt-1">{totalFiles}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">XLSX</span>
            <div className="text-3xl font-extrabold text-cyan-400 mt-1">{xlsxCount}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">PDF</span>
            <div className="text-3xl font-extrabold text-white mt-1">{pdfCount}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">DOCX</span>
            <div className="text-3xl font-extrabold text-white mt-1">{docxCount}</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-sm font-bold text-white">Реестр документов</h3>
            <p className="text-xs text-slate-400 mt-0.5">Добавить, заменить или удалить файл.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold text-xs transition-all shadow active:scale-95"
          >
            <PlusSymbolIcon className="w-4 h-4" />
            <span>+ Добавить файл</span>
          </button>
        </div>

        <div className="space-y-3">
          {docs.map((doc) => (
            <div
              key={doc.id}
              className="p-4 rounded-xl bg-[#061420] border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            >
              <div className="min-w-0 pr-2">
                <h4 className="text-xs font-bold text-white truncate">{doc.filename}</h4>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {doc.file_type} · {doc.version} · обновлено {doc.updated_date}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => handleReplace(doc.id)}
                  className="px-3 py-1.5 rounded-lg border border-cyan-500/30 bg-[#0c2336] hover:bg-[#112f48] text-cyan-300 text-xs font-semibold transition-all active:scale-95"
                >
                  Заменить
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(doc.id)}
                  className="px-3 py-1.5 rounded-lg border border-rose-500/30 bg-[#1c111a] hover:bg-[#281524] text-rose-300 text-xs font-semibold transition-all active:scale-95"
                >
                  Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showAddModal && (
        <div className="cab-overlay">
          <div className="cab-shell cab-shell--wide cab-shell--tall">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white">Добавить кафедральный файл</h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <CloseCrossIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddDocument} className="mt-4 space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                  Название файла
                </label>
                <input
                  type="text"
                  value={newFilename}
                  onChange={(e) => setNewFilename(e.target.value)}
                  placeholder="Например: План НИР кафедры.docx"
                  className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                  required
                />
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                  Формат
                </label>
                <select
                  value={newFileType}
                  onChange={(e) => setNewFileType(e.target.value)}
                  className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="DOCX">DOCX</option>
                  <option value="XLSX">XLSX</option>
                  <option value="PDF">PDF</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 text-xs font-semibold hover:bg-slate-700"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={submitting || !newFilename.trim()}
                  className="px-5 py-2 rounded-xl bg-amber-400 text-slate-950 font-bold text-xs hover:bg-amber-300 disabled:opacity-50"
                >
                  {submitting ? 'Сохранение...' : 'Добавить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
